import React, { useState, useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import Sidebar from './Sidebar';
import MobileNavigation from './mobile-navigation';
import { useSidebar } from '../../context/sidebar-context';
import EmailVerificationBanner from '../ui/email-verification-banner';
import { useProfessionalViewModel } from '../../view-model/professional/professional-view-model';
import { observer } from 'mobx-react-lite';
import OnboardingModal from '../onboarding/onboarding-modal';
import companyStore from '../../store/company-store';
import subscriptionStore from '../../store/subscription-store';
import { subscriptionViewModel } from '../../view-model/professional/subscription-view-model';
import { SubscriptionLimitProvider } from '../subscription/subscription-limit-provider';

const ProviderLayout: React.FC = observer(() => {
  const { isSidebarOpen, isCollapsed } = useSidebar();
  const professionalViewModel = useProfessionalViewModel();
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadProfessionalProfile = async () => {
      try {
        // Charger le profil et l'abonnement en parallèle
        await Promise.all([
          professionalViewModel.handleGetProfil(),
          subscriptionViewModel.fetchCurrentSubscription()
        ]);

        if (companyStore.hasNoOwnedCompanies && companyStore.isNoCompanies) {
          setShowOnboarding(true);
        }
      } catch (error) {
        console.error("Erreur lors du chargement du profil professionnel:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadProfessionalProfile().then();
  }, [professionalViewModel]);

  const hasActiveSubscription = subscriptionStore.isActive || subscriptionStore.isTrialing;

  const handleOnboardingComplete = () => {
    setShowOnboarding(false);
    localStorage.removeItem('onboarding_selected_plan');
  };

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <SubscriptionLimitProvider>
      {/* Modal d'onboarding */}
      <OnboardingModal
        isOpen={showOnboarding}
        onComplete={handleOnboardingComplete}
        hasActiveSubscription={hasActiveSubscription}
      />

      <div className="flex h-screen overflow-hidden bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white">
        {/* Bannière de vérification d'email */}
        <EmailVerificationBanner />

        {/* Sidebar pour desktop */}
        <Sidebar />

        {/* Navigation mobile */}
        <MobileNavigation />

        <div className={`flex flex-col flex-1 min-w-0 transition-all duration-300 ${
            isSidebarOpen ? (isCollapsed ? 'lg:ml-16' : 'lg:ml-52') : ''
        } pb-16 lg:pb-0`}>
          <main className="flex-1 min-h-0 p-4 overflow-y-auto">
            <div className="h-full">
              <Outlet />
            </div>
          </main>
        </div>
      </div>
    </SubscriptionLimitProvider>
  );
});

export default ProviderLayout;